import { useState, useEffect } from "react";
import { FiPhone, FiPlus, FiArrowLeft, FiX } from "react-icons/fi";
import { useBusiness } from "./business_context";
import FormTab from "./formtab/formtab";
import QandATab from "./qandatab/qandatab";
import WorkingHoursTab from "./workinghourstab/workinghourstab";
import AddNewLineDialog from "./AddNewLineDialog";
import ReservedPackageManagerDialog from "./ReservedPackageManagerDialog";

export default function Business() {
  const {
    businessLines,
    isLoading,
    selectedLineId,
    setSelectedLineId,
    addBusinessLine,
    refetchBusinessLines,
    hasFormChanges,
    hasQaChanges,
    hasWorkingHoursChanges,
    hasAnyChanges,
    resetFormData,
    resetQaData,
    resetWorkingHoursData,
    selectedLineStatus,
    isLineActive,
    isLineReserved,
    isLinePendingActivation,
    isLineInactive,
    canEditLine,
  } = useBusiness();

  const [activeSubTab, setActiveSubTab] = useState("form");
  const [showAddLineDialog, setShowAddLineDialog] = useState(false);
  const [newlyAddedLineId, setNewlyAddedLineId] = useState<string | null>(null);
  const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
    useState(false);
  const [pendingTabChange, setPendingTabChange] = useState<string | null>(null);
  const [showReservedDialog, setShowReservedDialog] = useState(false);
  const [selectedReservedLine, setSelectedReservedLine] = useState<any>(null);

  // Function to check if there are any unsaved changes
  const hasUnsavedChanges = () => {
    return hasAnyChanges();
  };

  // Handle tab change with unsaved changes check
  const handleTabChange = (tabName: string) => {
    // If line is not editable, allow tab switching without unsaved changes check
    if (!canEditLine) {
      setActiveSubTab(tabName);
      return;
    }

    if (hasUnsavedChanges()) {
      setPendingTabChange(tabName);
      setShowUnsavedChangesDialog(true);
      return;
    }
    setActiveSubTab(tabName);
  };

  // Handle discard changes and leave
  const handleDiscardAndLeave = () => {
    console.log("Business: handleDiscardAndLeave called");
    // Reset all data immediately
    resetFormData();
    resetQaData();
    resetWorkingHoursData();
    setSelectedLineId(null);
  };

  // Handle dialog actions
  const handleDiscardAndSwitch = () => {
    console.log("Business: handleDiscardAndSwitch called", {
      pendingTabChange,
    });

    // Reset all data immediately
    resetFormData();
    resetQaData();
    resetWorkingHoursData();

    // Switch to pending tab
    if (pendingTabChange) {
      setActiveSubTab(pendingTabChange);
    }
    setShowUnsavedChangesDialog(false);
    setPendingTabChange(null);
  };

  const handleStayOnTab = () => {
    setShowUnsavedChangesDialog(false);
    setPendingTabChange(null);
  };

  // Handle business line card clicks based on status
  const handleLineClick = (line: any) => {
    if (line.status === "pending_activation") {
      // Do nothing for pending activation lines
      return;
    }

    if (line.status === "reserved") {
      // Open reserved package manager dialog
      setSelectedReservedLine(line);
      setShowReservedDialog(true);
      return;
    }

    // For active and inactive lines, select the line
    setSelectedLineId(line.id);
  };

  // Reset change states when business line changes
  useEffect(() => {
    // The context will automatically clear changed data when selectedLineId changes
  }, [selectedLineId]);

  if (isLoading) {
    return (
      <div className="flex-1 overflow-auto">
        <div className="mx-auto py-8 px-8">
          <div className="animate-pulse space-y-4">
            {[...Array(3)].map((_, index) => (
              <div key={index} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto">
      <div className="mx-auto py-8 px-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            {selectedLineId ? (
              <div className="flex items-center gap-4">
                <button
                  onClick={() => {
                    setSelectedLineId(null);
                  }}
                  className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  <FiArrowLeft size={20} />
                  Back
                </button>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-3">
                    {
                      businessLines.find((line) => line.id === selectedLineId)
                        ?.phone_number
                    }
                    {isLineInactive && (
                      <span className="text-sm bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full font-medium">
                        Read-only mode
                      </span>
                    )}
                    {hasUnsavedChanges() && isLineActive && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm bg-red-100 text-red-800 px-2 py-1 rounded-full">
                          Unsaved changes
                        </span>
                        <button
                          onClick={handleDiscardAndLeave}
                          className="text-sm bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-md transition-colors"
                        >
                          Discard Changes
                        </button>
                      </div>
                    )}
                  </h1>
                  <p className="text-gray-600">
                    {isLineInactive
                      ? `This line is currently ${selectedLineStatus}. You can view the data but cannot make changes.`
                      : isLineReserved
                      ? "This line is reserved. Use the reserved package manager to activate it."
                      : isLinePendingActivation
                      ? `This line is currently ${selectedLineStatus}. Please wait for activation.`
                      : "Configure this business line."}
                  </p>
                </div>
              </div>
            ) : (
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  Business Lines
                </h1>
                <p className="text-gray-600">
                  Setup and configure receptionist lines.
                </p>
              </div>
            )}
          </div>
          {!selectedLineId && (
            <button
              onClick={() => setShowAddLineDialog(true)}
              className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 transition-colors"
            >
              <FiPlus size={20} />
              Add new
            </button>
          )}
        </div>

        {/* Business Lines Cards - Only show when no line is selected */}
        {!selectedLineId && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {businessLines.map((line) => (
                <div
                  key={line.id}
                  className={`rounded-lg shadow-md p-6 transition-all hover:shadow-lg ${
                    line.status === "pending_activation"
                      ? "bg-gray-50 border-2 border-gray-300 cursor-not-allowed opacity-60"
                      : newlyAddedLineId === line.id
                      ? "bg-green-50 border-2 border-green-300 ring-2 ring-green-200 cursor-pointer"
                      : line.status === "reserved"
                      ? "bg-blue-50 border-2 border-blue-300 cursor-pointer"
                      : line.status !== "active"
                      ? "bg-red-50 border-2 border-red-200 cursor-pointer"
                      : "bg-white cursor-pointer"
                  }`}
                  onClick={() => handleLineClick(line)}
                >
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <p className="text-gray-600 font-medium">
                        {line.phone_number}
                      </p>
                      <div className="flex items-center gap-2">
                        {line.status !== "active" && (
                          <span
                            className={`text-xs px-2 py-1 rounded-full font-medium ${
                              line.status === "reserved"
                                ? "bg-blue-100 text-blue-800"
                                : line.status === "pending_activation"
                                ? "bg-yellow-100 text-yellow-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {line.status === "inactive"
                              ? "Inactive"
                              : line.status === "suspended"
                              ? "Suspended"
                              : line.status === "pending_activation"
                              ? "Pending Activation"
                              : line.status === "reserved"
                              ? "Reserved"
                              : line.status}
                          </span>
                        )}
                        {newlyAddedLineId === line.id && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                            New!
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500 capitalize">
                        {line.line_type === "toll_free"
                          ? "Toll Free"
                          : "Local DID"}
                      </span>
                      <span className="text-xs text-gray-500">
                        {line.service_type === "answering"
                          ? "Answering Service"
                          : line.service_type}
                      </span>
                    </div>
                    {line.status !== "active" && (
                      <div className="mt-2 p-2 bg-red-50 rounded-md">
                        <p className="text-xs text-red-600">
                          This line is currently {line.status}. Contact support
                          for assistance.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Tabs - Show when a line is selected */}
        {selectedLineId && (
          <>
            <div className="border-b border-gray-200 mb-6">
              <div className="flex space-x-8">
                <button
                  className={`pb-4 flex items-center gap-2 ${
                    activeSubTab === "form"
                      ? "text-[#78C7D1] border-b-2 border-[#78C7D1]"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => handleTabChange("form")}
                >
                  Form
                  {hasFormChanges() && (
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  )}
                </button>
                <button
                  className={`pb-4 flex items-center gap-2 ${
                    activeSubTab === "qa"
                      ? "text-[#78C7D1] border-b-2 border-[#78C7D1]"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => handleTabChange("qa")}
                >
                  Q&A
                  {hasQaChanges() && (
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  )}
                </button>
                <button
                  className={`pb-4 flex items-center gap-2 ${
                    activeSubTab === "working_hours"
                      ? "text-[#78C7D1] border-b-2 border-[#78C7D1]"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                  onClick={() => handleTabChange("working_hours")}
                >
                  Working Hours
                  {hasWorkingHoursChanges() && (
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  )}
                </button>
              </div>
            </div>

            {/* Tab Content */}
            <div className="mt-2">
              {activeSubTab === "form" && <FormTab />}
              {activeSubTab === "qa" && <QandATab />}
              {activeSubTab === "working_hours" && <WorkingHoursTab />}
            </div>
          </>
        )}

        {/* Empty state - Only show when no line is selected */}
        {!selectedLineId && businessLines.length === 0 && (
          <div className="text-center py-12">
            <FiPhone className="mx-auto text-gray-400 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No business lines
            </h3>
            <p className="text-gray-500 mb-6">
              Get started by adding your first business line.
            </p>
            <button
              onClick={() => setShowAddLineDialog(true)}
              className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-lg flex items-center gap-2 mx-auto transition-colors"
            >
              <FiPlus size={20} />
              Add new
            </button>
          </div>
        )}
      </div>

      {/* Add New Line Dialog */}
      <AddNewLineDialog
        isOpen={showAddLineDialog}
        onClose={() => setShowAddLineDialog(false)}
        onSuccess={async (newLine) => {
          // Handle successful addition of new line
          if (newLine) {
            // Add the new line locally first for immediate UI update
            addBusinessLine(newLine);
            // Mark this line as newly added for highlighting
            setNewlyAddedLineId(newLine.id);
            // Clear the highlight after 5 seconds
            setTimeout(() => {
              setNewlyAddedLineId(null);
            }, 5000);
            // Refresh the business lines in the background to get the real data
            setTimeout(() => {
              refetchBusinessLines();
            }, 1000);
          } else {
            // If no specific line returned, just refresh the list
            await refetchBusinessLines();
          }
          setShowAddLineDialog(false);
        }}
      />

      {/* Reserved Package Manager Dialog */}
      <ReservedPackageManagerDialog
        isOpen={showReservedDialog}
        onClose={() => {
          setShowReservedDialog(false);
          setSelectedReservedLine(null);
        }}
        businessLine={selectedReservedLine}
      />

      {/* Unsaved Changes Dialog */}
      {showUnsavedChangesDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Unsaved Changes
              </h3>
              <button
                onClick={handleStayOnTab}
                className="text-gray-400 hover:text-gray-600"
                title="Close dialog"
                aria-label="Close dialog"
              >
                <FiX size={20} />
              </button>
            </div>
            <p className="text-gray-600 mb-6">
              You have unsaved changes. What would you like to do?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={handleStayOnTab}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
              >
                Stay on this tab
              </button>
              <button
                onClick={handleDiscardAndSwitch}
                className="px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors"
              >
                Discard changes and switch
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
